import { Button, Flex, Select, Tag, Tooltip, Typography } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { SelectProps } from 'antd';
import { SlaLabeledSelect } from '~/app/landing/sla/shared/components/SlaLabeledSelect';
import type {
  MaintenanceContractIssue,
  MaintenanceSlaDraftScopeRule,
  MaintenanceSlaInterventionMode,
  MaintenanceSlaScopeInterventionMode,
  MaintenanceSlaScopeOption,
  MaintenanceSlaScopeHierarchyAsset,
  MaintenanceSlaScopeOptions,
} from '~/app/landing/sla/oems/components/MaintenanceSlaContract/types';
import {
  MAINTENANCE_SCOPE_DIMENSION_OPTIONS,
  MAINTENANCE_SCOPE_EFFECT_OPTIONS,
} from '~/app/landing/sla/oems/components/MaintenanceSlaContract/utils';
import {
  isDeprecatedMaintenanceScopeDimension,
  MAINTENANCE_SLA_SCOPE_INTERVENTION_MODES,
} from '~/app/landing/sla/oems/components/MaintenanceSlaContract/types';
import { useMaintenanceSlaIssueHelp } from '~/app/landing/sla/oems/components/MaintenanceSlaContract/hooks/useMaintenanceSlaIssueHelp';
import { scopeOptionsForDimension } from '~/app/landing/sla/oems/components/MaintenanceSlaContract/hooks/useMaintenanceSlaScopeOptions';
import { isScopeValueBlockedByOppositeEffectInDraft } from '@evimo/core/modules/partner-maintenance-rules/domain/partner-maintenance-scope-effect-conflict.utils';
import {
  isDraftScopeValueBlockedByBroaderInclude,
  type ScopeHierarchyAsset,
} from '@evimo/core/modules/partner-maintenance-rules/domain/partner-maintenance-scope-hierarchy.utils';
import type { PartnerMaintenanceScopeDimension } from '@evimo/types/enums/partner-maintenance-rules';
import {
  findBlockingScopeClaim,
  scopeRowIdentityKey,
} from '~/app/landing/sla/oems/components/MaintenanceSlaContract/utils/scope-claim.utils';
import {
  deprecatedTagStyle,
  scopeRuleActionsColStyle,
  scopeRuleDimensionColStyle,
  scopeRuleEffectColStyle,
  scopeRuleFieldsRowStyle,
  scopeRuleHelpStyle,
  scopeRuleModeColStyle,
  scopeRuleRowHoverStyle,
  scopeRuleRowStyle,
  scopeRuleValuesColStyle,
  scopeRulesBoxStyle,
  scopeRulesEmptyStyle,
  scopeRulesHeaderStyle,
  scopeRulesHintStyle,
  scopeRulesListStyle,
  scopeRulesTitleStyle,
  scopeRulesWrapperStyle,
} from './MaintenanceSlaScopeRulesEditor.style';

const DEPRECATED_TOOLTIP =
  'This scope dimension was retired on 2026-10-08; edit is disabled';

const SCOPE_INTERVENTION_MODE_OPTIONS =
  MAINTENANCE_SLA_SCOPE_INTERVENTION_MODES.map((value) => ({ value }));

export interface MaintenanceSlaScopeRulesEditorProps {
  rules: MaintenanceSlaDraftScopeRule[];
  issues: readonly MaintenanceContractIssue[];
  scopeOptions: MaintenanceSlaScopeOptions;
  scopeAssetHierarchy: MaintenanceSlaScopeHierarchyAsset[];
  scopeOptionsLoading: boolean;
  interventionMode: MaintenanceSlaInterventionMode | null;
  onAdd: () => void;
  onChange: (
    ruleId: string,
    patch: Partial<MaintenanceSlaDraftScopeRule>,
  ) => void;
  onRemove: (ruleId: string) => void;
}

export function MaintenanceSlaScopeRulesEditor({
  rules,
  issues,
  scopeOptions,
  scopeAssetHierarchy,
  scopeOptionsLoading,
  interventionMode,
  onAdd,
  onChange,
  onRemove,
}: MaintenanceSlaScopeRulesEditorProps) {
  const { t } = useTranslation('sla');
  const help = useMaintenanceSlaIssueHelp(issues);

  const valuesUsedInOtherRows = (selfRuleId: string): Set<string> => {
    const acc = new Set<string>();
    for (const rule of rules) {
      if (rule.id === selfRuleId) continue;
      for (const value of rule.values) {
        if (!value) continue;
        acc.add(
          scopeRowIdentityKey(
            rule.dimension,
            rule.effect,
            value,
            rule.interventionMode,
            interventionMode,
          ),
        );
      }
    }
    return acc;
  };

  const buildClaimedMultiOptions = (
    selfRuleId: string,
    ruleDim: string,
    ruleEffect: string,
    ruleScopeMode: MaintenanceSlaScopeInterventionMode | null | undefined,
    options: MaintenanceSlaScopeOption[],
  ): NonNullable<SelectProps['options']> => {
    const blocked = valuesUsedInOtherRows(selfRuleId);
    return options
      .filter((option) => {
        if (findBlockingScopeClaim(option, ruleScopeMode, interventionMode)) {
          return false;
        }
        if (
          isScopeValueBlockedByOppositeEffectInDraft({
            selfRuleId,
            selfEffect: ruleEffect as MaintenanceSlaDraftScopeRule['effect'],
            dimension: ruleDim as PartnerMaintenanceScopeDimension,
            value: option.value,
            ruleScopeMode,
            rules,
            headerMode: interventionMode,
          })
        ) {
          return false;
        }
        if (
          ruleEffect === 'INCLUDE' &&
          scopeAssetHierarchy.length > 0 &&
          isDraftScopeValueBlockedByBroaderInclude({
            selfRuleId,
            dimension: ruleDim as PartnerMaintenanceScopeDimension,
            value: option.value,
            ruleScopeMode,
            rules,
            headerMode: interventionMode,
            assets: scopeAssetHierarchy as ScopeHierarchyAsset[],
          })
        ) {
          return false;
        }
        return true;
      })
      .map((option) => {
        const duplicate = blocked.has(
          scopeRowIdentityKey(
            ruleDim,
            ruleEffect,
            option.value,
            ruleScopeMode,
            interventionMode,
          ),
        );
        let title: string | undefined;
        if (duplicate) {
          title =
            'This value is already in another scope rule row with the same dimension, effect, and intervention mode';
        }
        const label = duplicate ? `${option.label} (duplicate)` : option.label;
        return {
          value: option.value,
          label,
          disabled: duplicate,
          title,
        };
      });
  };

  return (
    <div style={scopeRulesWrapperStyle}>
      <div style={scopeRulesBoxStyle}>
        <Flex
          align="flex-start"
          justify="space-between"
          gap={12}
          wrap
          style={scopeRulesHeaderStyle}
        >
          <div>
            <Typography.Text style={scopeRulesTitleStyle}>
              {t('oems.maintenance.sections.scopeRules')}
            </Typography.Text>
            <Typography.Text style={scopeRulesHintStyle}>
              {t('oems.maintenance.scopeRulesHint')}
            </Typography.Text>
          </div>
          <Button type="primary" icon={<PlusOutlined />} onClick={onAdd}>
            {t('oems.maintenance.addScopeRule')}
          </Button>
        </Flex>

        {rules.length === 0 ? (
          <Typography.Paragraph style={scopeRulesEmptyStyle}>
            {t('oems.maintenance.noScopeRules')}
          </Typography.Paragraph>
        ) : (
          <div style={scopeRulesListStyle}>
            {rules.map((rule) => {
              const deprecated = isDeprecatedMaintenanceScopeDimension(
                rule.dimension,
              );
              const valueHelp =
                help('scopeEffectConflict', { ruleId: rule.id }) ??
                help('scopeHierarchyRedundancy', { ruleId: rule.id }) ??
                help('scopeValue', { ruleId: rule.id });
              const showModeCol = interventionMode === 'BOTH';

              return (
                <div
                  key={rule.id}
                  style={scopeRuleRowStyle}
                  onMouseEnter={(e) => {
                    Object.assign(
                      e.currentTarget.style,
                      scopeRuleRowHoverStyle,
                    );
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#e2e8f0';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <Flex
                    gap={12}
                    wrap
                    align="flex-start"
                    style={scopeRuleFieldsRowStyle}
                  >
                    <div style={scopeRuleDimensionColStyle}>
                      <SlaLabeledSelect
                        style={{ minWidth: 180 }}
                        value={rule.dimension}
                        options={MAINTENANCE_SCOPE_DIMENSION_OPTIONS}
                        translationPrefix="oems.maintenance.dimension"
                        disabled={deprecated}
                        onChange={(dimension) =>
                          onChange(rule.id, {
                            dimension,
                            values:
                              dimension === rule.dimension ? rule.values : [],
                          })
                        }
                      />
                      {deprecated ? (
                        <Tooltip title={DEPRECATED_TOOLTIP}>
                          <Tag style={deprecatedTagStyle}>Deprecated</Tag>
                        </Tooltip>
                      ) : null}
                    </div>
                    <div style={scopeRuleEffectColStyle}>
                      <SlaLabeledSelect
                        style={{ minWidth: 140 }}
                        value={rule.effect}
                        options={MAINTENANCE_SCOPE_EFFECT_OPTIONS}
                        translationPrefix="oems.maintenance.effect"
                        disabled={deprecated}
                        onChange={(effect) => onChange(rule.id, { effect })}
                      />
                    </div>
                    <div style={scopeRuleValuesColStyle}>
                      <Select
                        mode="multiple"
                        maxTagCount="responsive"
                        showSearch
                        optionFilterProp="label"
                        style={{ width: '100%' }}
                        loading={scopeOptionsLoading}
                        placeholder={t('oems.maintenance.selectValue')}
                        disabled={deprecated}
                        value={rule.values}
                        options={buildClaimedMultiOptions(
                          rule.id,
                          rule.dimension,
                          rule.effect,
                          rule.interventionMode,
                          scopeOptionsForDimension(
                            scopeOptions,
                            rule.dimension,
                          ),
                        )}
                        onChange={(values: string[]) =>
                          onChange(rule.id, { values })
                        }
                      />
                    </div>
                    {showModeCol ? (
                      <div style={scopeRuleModeColStyle}>
                        <Select
                          style={{ width: '100%' }}
                          allowClear
                          placeholder={t('oems.maintenance.selectMode')}
                          disabled={deprecated}
                          value={rule.interventionMode ?? undefined}
                          options={SCOPE_INTERVENTION_MODE_OPTIONS.map(
                            ({ value }) => ({
                              value,
                              label: t(`oems.maintenance.mode.${value}`),
                            }),
                          )}
                          onChange={(
                            value:
                              | MaintenanceSlaScopeInterventionMode
                              | undefined,
                          ) =>
                            onChange(rule.id, {
                              interventionMode: value ?? null,
                            })
                          }
                        />
                      </div>
                    ) : null}
                    <div style={scopeRuleActionsColStyle}>
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        disabled={deprecated}
                        onClick={() => onRemove(rule.id)}
                      />
                    </div>
                  </Flex>
                  {valueHelp ? (
                    <Typography.Text style={scopeRuleHelpStyle}>
                      {valueHelp}
                    </Typography.Text>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
